"use client";

import { useMemo, useRef, useState, type CSSProperties } from "react";
import { PDFDocument } from "pdf-lib";
import { CatalogAutocomplete, extractGtinFromScan, resolveCatalogEan, type CatalogSuggestion } from "./catalog-autocomplete";

type AdviceTheme = "orange" | "blue" | "green";
type AdviceProduct = {
  id: string;
  query: string;
  ean: string;
  name: string;
  role: string;
  dosage: string;
  instructions: string;
};

const pharmacyDetails = {
  Square: {
    name: "Pharmacie du Square",
    address: "613 av. de la Libération, 06700 Saint-Laurent-du-Var",
    phone: "04 93 31 25 75",
    website: "www.pharmaciedusquare06700.com",
  },
  Ragadan: {
    name: "Pharmacie du Ragadan",
    address: "324 bd de Provence, 06700 Saint-Laurent-du-Var",
    phone: "04 93 07 25 69",
    website: "www.pharmacieduragadan.com",
  },
  Laurentine: {
    name: "Pharmacie Laurentine",
    address: "650 av. du Général de Gaulle, 06700 Saint-Laurent-du-Var",
    phone: "04 93 31 00 55",
    website: "www.pharmacielaurentine.fr",
  },
} as const;

const themeDetails = {
  orange: { label: "Orange", accent: "#e66718", soft: "#fff4e8" },
  blue: { label: "Bleu", accent: "#1769d2", soft: "#eaf4ff" },
  green: { label: "Vert", accent: "#0b8b72", soft: "#e9f9f3" },
} as const;

const today = () => new Date().toISOString().slice(0, 10);
const makeProduct = (): AdviceProduct => ({
  id: crypto.randomUUID(),
  query: "",
  ean: "",
  name: "",
  role: "",
  dosage: "",
  instructions: "",
});

function displayDate(value: string) {
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function adviceLines(value: string) {
  return value
    .split(/\n|\s[·•]\s/)
    .map((line) => line.replace(/^[✓✔⚠❌•·\-–—\s]+/, "").trim())
    .filter(Boolean);
}

export function AdviceSheetModule({
  onBack,
  initialPharmacy,
  initialOperator,
}: {
  onBack: () => void;
  initialPharmacy: keyof typeof pharmacyDetails;
  initialOperator: string;
}) {
  const [theme, setTheme] = useState<AdviceTheme>("orange");
  const [civilTitle, setCivilTitle] = useState("");
  const [patientName, setPatientName] = useState("");
  const [date, setDate] = useState(today());
  const [email, setEmail] = useState("");
  const [profile, setProfile] = useState<"adulte" | "enfant" | "personnalise">("adulte");
  const [age, setAge] = useState("");
  const [weight, setWeight] = useState("");
  const [objective, setObjective] = useState("");
  const [operator, setOperator] = useState(initialOperator);
  const [products, setProducts] = useState<AdviceProduct[]>([makeProduct()]);
  const [scanValue, setScanValue] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [lifestyle, setLifestyle] = useState("");
  const [transmission, setTransmission] = useState("");
  const [prevention, setPrevention] = useState("");
  const [takeaway, setTakeaway] = useState("Suivre les conseils remis par l’équipe officinale et recontacter la pharmacie si besoin.");
  const [verified, setVerified] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const scanInputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLElement>(null);

  const pharmacy = pharmacyDetails[initialPharmacy];
  const palette = themeDetails[theme];
  const completedProducts = useMemo(() => products.filter((product) => product.name.trim()), [products]);
  const verificationIssues = useMemo(() => {
    const issues: string[] = [];
    if (!patientName.trim()) issues.push("le nom du patient");
    if (!date) issues.push("la date");
    if (!objective.trim()) issues.push("le motif du conseil");
    if (!operator.trim()) issues.push("la personne qui remet le conseil");
    if (profile === "enfant" && !age.trim()) issues.push("l’âge de l’enfant");
    if (profile === "enfant" && !weight.trim()) issues.push("le poids de l’enfant");
    if (profile === "personnalise" && !age.trim()) issues.push("l’âge personnalisé");
    if (!completedProducts.length) issues.push("au moins un produit");
    return issues;
  }, [age, completedProducts, date, objective, operator, patientName, profile, weight]);

  const invalidateVerification = () => {
    setVerified(false);
    setNotice("");
  };

  const updateProduct = (id: string, patch: Partial<AdviceProduct>) => {
    setProducts((current) => current.map((product) => product.id === id ? { ...product, ...patch } : product));
    setVerified(false);
  };

  const pickProduct = (id: string, suggestion: CatalogSuggestion) => {
    updateProduct(id, { query: suggestion.product, name: suggestion.product, ean: suggestion.ean });
  };

  const addScannedProduct = async () => {
    const ean = extractGtinFromScan(scanValue);
    if (!ean) {
      setNotice("Scannez ou saisissez un code EAN/DataMatrix valide.");
      scanInputRef.current?.focus();
      return;
    }
    setScanLoading(true);
    setNotice("");
    try {
      const suggestion = await resolveCatalogEan(ean);
      if (!suggestion) {
        setNotice("Produit introuvable dans la base LGPI. Vous pouvez le saisir manuellement.");
        return;
      }
      setProducts((current) => {
        const empty = current.find((product) => !product.name.trim());
        if (empty) {
          return current.map((product) => product.id === empty.id
            ? { ...product, query: suggestion.product, name: suggestion.product, ean: suggestion.ean }
            : product);
        }
        return [...current, { ...makeProduct(), query: suggestion.product, name: suggestion.product, ean: suggestion.ean }];
      });
      setScanValue("");
      setVerified(false);
      setNotice(`${suggestion.product} ajouté depuis la base LGPI.`);
      scanInputRef.current?.focus();
    } catch {
      setNotice("La base LGPI est momentanément indisponible.");
    } finally {
      setScanLoading(false);
    }
  };

  const analyzeWithAi = async () => {
    if (completedProducts.length === 0) {
      setNotice("Ajoutez au moins un produit : l’IA remplira ensuite la fiche.");
      return;
    }
    if (profile === "enfant" && (!age.trim() || !weight.trim())) {
      setNotice("Pour un enfant, renseignez et vérifiez l’âge et le poids avant l’analyse IA.");
      return;
    }
    setAiLoading(true);
    setNotice("");
    try {
      const response = await fetch("/api/advice-sheet-ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profile,
          age,
          weight,
          objective,
          products: completedProducts.map(({ name, ean, role, dosage, instructions }) => ({ name, ean, role, dosage, instructions })),
          lifestyle,
          transmission,
          prevention,
          takeaway,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Analyse indisponible");
      if (typeof payload.objective === "string") setObjective(payload.objective);
      if (typeof payload.lifestyle === "string") setLifestyle(payload.lifestyle);
      if (typeof payload.transmission === "string") setTransmission(payload.transmission);
      if (typeof payload.prevention === "string") setPrevention(payload.prevention);
      if (typeof payload.takeaway === "string") setTakeaway(payload.takeaway);
      if (Array.isArray(payload.products)) {
        let completedIndex = 0;
        setProducts((current) => current.map((product) => {
          if (!product.name.trim()) return product;
          const suggestion = payload.products[completedIndex++] ?? payload.products.find((row: { name?: string }) => row.name?.toLocaleLowerCase("fr") === product.name.toLocaleLowerCase("fr"));
          return suggestion ? {
            ...product,
            role: suggestion.role || product.role,
            dosage: product.dosage || suggestion.dosage,
            instructions: suggestion.instructions || product.instructions,
          } : product;
        }));
      }
      setVerified(false);
      setNotice("La fiche a été remplie par l’IA. Relisez et corrigez si nécessaire avant l’export.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "L’analyse IA n’a pas abouti.");
    } finally {
      setAiLoading(false);
    }
  };

  const renderPreviewCanvas = async () => {
    const source = previewRef.current;
    if (!source) throw new Error("L’aperçu de la fiche n’est pas disponible.");
    await document.fonts?.ready;
    const clone = source.cloneNode(true) as HTMLElement;
    const originals = [source, ...Array.from(source.querySelectorAll<HTMLElement>("*"))];
    const copies = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>("*"))];
    originals.forEach((element, index) => {
      const computed = window.getComputedStyle(element);
      copies[index].style.cssText = Array.from({ length: computed.length }, (_, propertyIndex) => computed.item(propertyIndex)).map((property) => `${property}:${computed.getPropertyValue(property)};`).join("");
    });
    const width = Math.ceil(source.scrollWidth);
    const height = Math.ceil(source.scrollHeight);
    clone.style.width = `${width}px`;
    clone.style.height = `${height}px`;
    clone.style.margin = "0";
    clone.style.boxShadow = "none";
    const markup = new XMLSerializer().serializeToString(clone);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml">${markup}</div></foreignObject></svg>`;
    const image = new Image();
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    await image.decode();
    const scale = 2.4;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Le rendu PDF n’a pas pu être préparé.");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas;
  };

  const buildPdf = async () => {
    const canvas = await renderPreviewCanvas();
    const pdf = await PDFDocument.create();
    const pageWidth = 420;
    const pageHeight = 595;
    const pixelsPerPage = Math.round(canvas.width * pageHeight / pageWidth);
    for (let top = 0; top < canvas.height; top += pixelsPerPage) {
      const sliceHeight = Math.min(pixelsPerPage, canvas.height - top);
      const slice = document.createElement("canvas");
      slice.width = canvas.width;
      slice.height = sliceHeight;
      const context = slice.getContext("2d");
      if (!context) throw new Error("Le rendu PDF n’a pas pu être finalisé.");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, slice.width, slice.height);
      context.drawImage(canvas, 0, top, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
      const image = await pdf.embedPng(slice.toDataURL("image/png", 1));
      const drawnHeight = pageWidth * sliceHeight / canvas.width;
      const page = pdf.addPage([pageWidth, pageHeight]);
      page.drawImage(image, { x: 0, y: pageHeight - drawnHeight, width: pageWidth, height: drawnHeight });
    }
    return pdf.save();
  };

  const makePdfFile = async () => {
    const bytes = await buildPdf();
    const filename = `fiche-conseil-${patientName.trim() || "patient"}-${date}.pdf`;
    const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    return new File([buffer], filename, { type: "application/pdf" });
  };

  const downloadFile = (file: File) => {
    const url = URL.createObjectURL(file);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = file.name;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  const prepareAttachedEmail = async (file: File) => {
    const subject = "Votre fiche conseil";
    const body = `Bonjour,\n\nVous trouverez votre fiche conseil en pièce jointe.\n\nBien cordialement,\n${pharmacy.name}`;
    const shareData = { files: [file], title: subject, text: body };
    if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
      await navigator.share(shareData);
      setNotice("La fiche conseil a été transmise avec le PDF joint.");
      return;
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = "";
    for (let offset = 0; offset < bytes.length; offset += 0x8000) binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
    const attachment = btoa(binary).match(/.{1,76}/g)?.join("\r\n") ?? "";
    const boundary = `pharma-${crypto.randomUUID()}`;
    const message = [
      `To: ${email.trim()}`,
      `Subject: ${subject}`,
      "MIME-Version: 1.0",
      `Content-Type: multipart/mixed; boundary="${boundary}"`,
      "",
      `--${boundary}`,
      "Content-Type: text/plain; charset=UTF-8",
      "Content-Transfer-Encoding: 8bit",
      "",
      body,
      "",
      `--${boundary}`,
      `Content-Type: application/pdf; name="${file.name.replace(/[\"\r\n]/g, "_")}"`,
      "Content-Transfer-Encoding: base64",
      `Content-Disposition: attachment; filename="${file.name.replace(/[\"\r\n]/g, "_")}"`,
      "",
      attachment,
      `--${boundary}--`,
      "",
    ].join("\r\n");
    const emailFile = new File([message], `mail-fiche-conseil-${patientName.trim() || "patient"}.eml`, { type: "message/rfc822" });
    downloadFile(emailFile);
    setNotice("E-mail préparé avec le PDF déjà joint. Ouvrez le fichier .eml téléchargé, vérifiez puis envoyez.");
  };

  const exportPdf = async (mail = false) => {
    if (!verified) {
      setNotice("Confirmez d’abord que tous les éléments ont été vérifiés.");
      return;
    }
    if (mail) {
      if (!email.trim()) {
        setNotice("Ajoutez l’e-mail du patient pour préparer l’envoi.");
        return;
      }
    }
    let file: File;
    try {
      file = await makePdfFile();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Le PDF n’a pas pu être généré.");
      return;
    }
    if (mail) {
      try {
        await prepareAttachedEmail(file);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") setNotice("Envoi annulé. La fiche reste disponible à l’écran.");
        else setNotice(error instanceof Error ? error.message : "L’e-mail n’a pas pu être préparé.");
      }
    } else {
      downloadFile(file);
      setNotice("PDF A5 généré.");
    }
  };

  const confirmVerification = (checked: boolean) => {
    if (!checked) {
      setVerified(false);
      return;
    }
    if (verificationIssues.length) {
      setVerified(false);
      setNotice(`À compléter avant validation : ${verificationIssues.slice(0, 4).join(", ")}${verificationIssues.length > 4 ? "…" : "."}`);
      return;
    }
    setVerified(true);
    setNotice("Fiche contrôlée : l’impression et l’envoi sont maintenant disponibles.");
  };

  const resetSheet = () => {
    setCivilTitle("");
    setPatientName("");
    setDate(today());
    setEmail("");
    setProfile("adulte");
    setAge("");
    setWeight("");
    setObjective("");
    setOperator(initialOperator);
    setProducts([makeProduct()]);
    setScanValue("");
    setLifestyle("");
    setTransmission("");
    setPrevention("");
    setTakeaway("Suivre les conseils remis par l’équipe officinale et recontacter la pharmacie si besoin.");
    setVerified(false);
    setNotice("Nouvelle fiche prête.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="advice-module" style={{ "--advice-accent": palette.accent, "--advice-soft": palette.soft } as CSSProperties}>
      <header className="module-topbar">
        <button onClick={onBack}>← Accueil</button>
        <div className="brand"><span>📝</span><strong>Fiche conseil patient</strong></div>
        <span className="status-pill">A5 · aperçu en direct</span>
      </header>

      <section className="advice-hero">
        <div><span>CONSEIL OFFICINAL</span><h1>Créer une fiche claire, utile et vérifiée.</h1><p>La saisie reste simple. L’IA aide à structurer, le pharmacien garde la validation finale.</p></div>
        <div className="advice-theme-picker" aria-label="Style de la fiche">
          {(Object.keys(themeDetails) as AdviceTheme[]).map((key) => <button key={key} className={theme === key ? "active" : ""} onClick={() => setTheme(key)}><i style={{ background: themeDetails[key].accent }} />{themeDetails[key].label}</button>)}
        </div>
      </section>

      <div className="advice-workspace">
        <div className="advice-form-column">
          <section className="advice-card">
            <h2>👤 Patient</h2>
            <div className="advice-grid advice-grid-patient">
              <label><span>Civilité</span><select value={civilTitle} onChange={(event) => { setCivilTitle(event.target.value); invalidateVerification(); }}><option value="">—</option><option>Mme</option><option>M.</option><option>Enfant</option></select></label>
              <label><span>Nom du patient</span><input value={patientName} onChange={(event) => { setPatientName(event.target.value); invalidateVerification(); }} placeholder="Dupont" /></label>
              <label><span>Date</span><span className="advice-date-field"><input type="date" value={date} onChange={(event) => { setDate(event.target.value); invalidateVerification(); }} /><button onClick={() => { setDate(today()); invalidateVerification(); }}>Aujourd’hui</button></span></label>
            </div>
            <label><span>E-mail du patient (optionnel)</span><input type="email" value={email} onChange={(event) => { setEmail(event.target.value); invalidateVerification(); }} placeholder="patient@exemple.com" /></label>
            <div className="advice-choice-row"><span>Âge / profil patient</span>{(["adulte", "enfant", "personnalise"] as const).map((value) => <button key={value} className={profile === value ? "active" : ""} onClick={() => { setProfile(value); invalidateVerification(); }}>{value === "personnalise" ? "Âge personnalisé" : value[0].toUpperCase() + value.slice(1)}</button>)}</div>
            {profile !== "adulte" && <div className="advice-grid advice-age-grid">
              <label><span>Âge {profile === "enfant" ? "(obligatoire)" : ""}</span><input inputMode="numeric" value={age} onChange={(event) => { setAge(event.target.value.replace(/[^\d,.]/g, "")); invalidateVerification(); }} placeholder="Ex. 8" /></label>
              <label><span>Poids {profile === "enfant" ? "(obligatoire)" : "(si utile)"}</span><input inputMode="decimal" value={weight} onChange={(event) => { setWeight(event.target.value.replace(/[^\d,.]/g, "")); invalidateVerification(); }} placeholder="Ex. 27 kg" /></label>
            </div>}
            <p className="advice-helper">Facultatif pour un adulte. Pour un enfant, l’âge et le poids doivent être vérifiés avant l’analyse IA.</p>
          </section>

          <section className="advice-card">
            <h2>🎯 Motif / objectif du conseil</h2>
            <textarea value={objective} onChange={(event) => { setObjective(event.target.value); setVerified(false); }} placeholder="Ex. Soulager une toux sèche et rappeler les signes qui doivent amener à consulter." />
            <label><span>Conseillé par</span><input value={operator} onChange={(event) => { setOperator(event.target.value); invalidateVerification(); }} placeholder="Nom de l’opérateur" /></label>
          </section>

          <section className="advice-card">
            <h2>💊 Produits conseillés</h2>
            <div className="advice-scan-panel">
              <div><strong>Scanner un DataMatrix / EAN</strong><span>Le produit est repris automatiquement depuis la base LGPI.</span></div>
              <div className="advice-scan-row">
                <input
                  ref={scanInputRef}
                  inputMode="text"
                  value={scanValue}
                  onChange={(event) => setScanValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === "Tab") {
                      event.preventDefault();
                      void addScannedProduct();
                    }
                  }}
                  placeholder="Cliquez ici puis scannez le produit"
                />
                <button onClick={addScannedProduct} disabled={scanLoading}>{scanLoading ? "Recherche…" : "Ajouter depuis le scan"}</button>
              </div>
              <small>Base LGPI prête pour les fiches conseil.</small>
            </div>
            {products.map((product, index) => (
              <article className="advice-product-editor" key={product.id}>
                <header><strong>Produit {index + 1}</strong>{products.length > 1 && <button onClick={() => { setProducts((current) => current.filter((row) => row.id !== product.id)); invalidateVerification(); }}>Retirer</button>}</header>
                <label><span>Produit conseillé</span><CatalogAutocomplete value={product.query} confirmed={Boolean(product.ean)} onChange={(query) => updateProduct(product.id, { query, name: query, ean: "" })} onPick={(suggestion) => pickProduct(product.id, suggestion)} placeholder="Nom, EAN ou *4 derniers chiffres" inputMode="search" /></label>
                {product.ean && <small className="advice-ean">EAN {product.ean}</small>}
                <div className="advice-grid">
                  <label><span>Rôle du produit (optionnel)</span><textarea value={product.role} onChange={(event) => updateProduct(product.id, { role: event.target.value })} placeholder="Ex. Antiviral local indiqué pour le bouton de fièvre" /></label>
                  <label><span>Posologie validée (optionnelle)</span><textarea value={product.dosage} onChange={(event) => updateProduct(product.id, { dosage: event.target.value })} placeholder="Une consigne courte par ligne" /></label>
                </div>
                <label><span>Conseils d’utilisation (optionnels)</span><textarea value={product.instructions} onChange={(event) => updateProduct(product.id, { instructions: event.target.value })} placeholder="Une consigne courte par ligne" /></label>
              </article>
            ))}
            <button className="advice-add-product" onClick={() => { setProducts((current) => [...current, makeProduct()]); invalidateVerification(); }}>＋ Ajouter un produit</button>
          </section>

          <section className="advice-card">
            <h2>✔ Conseils pratiques</h2>
            <textarea value={lifestyle} onChange={(event) => { setLifestyle(event.target.value); setVerified(false); }} placeholder="Une recommandation courte par ligne…" />
            <h2>⚠ Éviter la transmission</h2>
            <textarea value={transmission} onChange={(event) => { setTransmission(event.target.value); setVerified(false); }} placeholder="Facultatif — gestes pour protéger l’entourage…" />
            <h2>☀ Prévention</h2>
            <textarea value={prevention} onChange={(event) => { setPrevention(event.target.value); setVerified(false); }} placeholder="Facultatif — limiter les récidives ou facteurs déclenchants…" />
            <h2>📌 Quand consulter / à retenir</h2>
            <textarea value={takeaway} onChange={(event) => { setTakeaway(event.target.value); setVerified(false); }} />
            <button className="advice-ai-button" onClick={analyzeWithAi} disabled={aiLoading}>{aiLoading ? "Analyse pharmaceutique en cours…" : "✨ Remplir et améliorer toute la fiche"}</button>
            <p className="advice-ai-note">L’IA remplit le rôle, la posologie, l’utilisation, les conseils pratiques, la transmission, la prévention et les signes d’alerte. Relisez et corrigez ensuite si besoin.</p>
          </section>
        </div>

        <aside className="advice-preview-column">
          <div className="advice-preview-sticky">
            <span className="advice-preview-label">APERÇU A5 EN DIRECT</span>
            <article className="advice-a5" ref={previewRef}>
              <header><div className="advice-a5-pharmacy"><span className="advice-a5-logo">✚</span><span><strong>{pharmacy.name}</strong><small>Votre conseil officinal personnalisé</small></span></div><div><span>CONSEIL OFFICINAL</span><h2>FICHE<br />CONSEIL</h2></div></header>
              <div className="advice-a5-rule" />
              <div className="advice-a5-patient">● Pour {`${civilTitle} ${patientName}`.trim() || "le patient"} · {displayDate(date)}{profile !== "adulte" && <> · {age ? `${age} ans` : "âge à préciser"}{weight && ` · ${weight} kg`}</>}</div>
              <section className="advice-a5-objective"><span>◎</span><div><small>VOTRE CONSEIL DU JOUR</small><p>{objective || "Conseils suite à l’entretien."}</p></div></section>
              {completedProducts.map((product) => <section className="advice-a5-product" key={product.id}><h3>{product.name}</h3>{product.role && <p><b>Pourquoi :</b> {product.role}</p>}{product.dosage && <><h4>Posologie</h4><ul>{adviceLines(product.dosage).map((item, index) => <li key={index}>{item}</li>)}</ul></>}{product.instructions && <><h4>Conseils d’utilisation</h4><ul>{adviceLines(product.instructions).map((item, index) => <li key={index}>{item}</li>)}</ul></>}</section>)}
              <div className="advice-a5-guidance">
                <section><h3>✔ Conseils pratiques</h3><ul>{adviceLines(lifestyle || "Compléter les conseils.").map((item, index) => <li key={index}>{item}</li>)}</ul></section>
                {transmission && <section><h3>⚠ Éviter la transmission</h3><ul>{adviceLines(transmission).map((item, index) => <li key={index}>{item}</li>)}</ul></section>}
                {prevention && <section><h3>☀ Prévention</h3><ul>{adviceLines(prevention).map((item, index) => <li key={index}>{item}</li>)}</ul></section>}
                <section><h3>📌 Quand consulter / à retenir</h3><ul>{adviceLines(takeaway).map((item, index) => <li key={index}>{item}</li>)}</ul></section>
              </div>
              <div className="advice-a5-warning">Cette fiche est une aide au bon usage. Elle ne remplace pas une consultation médicale ni une ordonnance.</div>
              <footer><strong>Conseil remis par {operator || "votre pharmacie"}</strong><small>{pharmacy.address}<br />{pharmacy.phone} · {pharmacy.website}</small></footer>
            </article>
            <label className={`advice-verification ${verified ? "checked" : ""}`}><input type="checkbox" checked={verified} onChange={(event) => confirmVerification(event.target.checked)} /><span><strong>J’ai vérifié tous les éléments remplis</strong><small>Patient, produits, posologies, conseils et coordonnées sont exacts.</small></span></label>
            {verificationIssues.length > 0 && <div className="advice-check-summary"><strong>{verificationIssues.length} contrôle{verificationIssues.length > 1 ? "s" : ""} restant{verificationIssues.length > 1 ? "s" : ""}</strong><span>{verificationIssues.slice(0, 3).join(" · ")}{verificationIssues.length > 3 ? "…" : ""}</span></div>}
            <div className="advice-export-row"><button className="primary" disabled={!verified} onClick={() => exportPdf(false)}>🖨️ Imprimer / PDF identique</button><button disabled={!verified || !email.trim()} onClick={() => exportPdf(true)}>✉️ E-mail avec PDF joint</button><button onClick={resetSheet}>＋ Nouvelle fiche</button></div>
            {notice && <p className="advice-notice">{notice}</p>}
          </div>
        </aside>
      </div>
    </main>
  );
}
